import { ChangeEvent, MouseEvent, useEffect, useState } from 'react'
import useSurveyLocale from '@aytm/app/swidget/base/modules/locale/useSurveyLocale'
import useViewport from '@aytm/app/swidget/base/modules/viewport/useViewport.ts'
import { useDebouncedCallback } from '@aytm/lib/hooks/useDebouncedCallback'
import style from './AddToCartButton.module.scss'

interface IAddToCartButtonProps {
  amount: number
  limit?: number
  disabled?: boolean
  appearance?: 'small' | 'big'
  // The detail page's buy box: a quantity field sits before the button and the − / + steppers.
  withInput?: boolean
  // The basket page's line: the field sits between − and +, with no "in basket" note under them.
  cartLine?: boolean
  onAdd: (qnt: number) => void
}

const AddToCartButton = ({
  amount,
  limit,
  disabled = false,
  appearance = 'big',
  withInput = false,
  cartLine = false,
  onAdd
}: IAddToCartButtonProps) => {
  const { t } = useSurveyLocale()
  const viewport = useViewport()
  const atCap = limit > 0 && amount >= limit
  const clamp = (value: number) => (limit > 0 ? Math.min(value, limit) : value)

  // Before the first add the field holds how many to add; after it, the quantity in the basket.
  const [draft, setDraft] = useState<string>(amount > 0 ? String(amount) : '1')
  useEffect(() => setDraft(amount > 0 ? String(amount) : '1'), [amount])

  // The buttons sit inside clickable cards; a quantity change must not open the product page.
  const change = (qnt: number) => (event: MouseEvent) => {
    event.stopPropagation()
    onAdd(qnt)
  }

  const onAddDraft = (event: MouseEvent) => {
    event.stopPropagation()
    const value = parseInt(draft, 10)
    onAdd(clamp(Number.isFinite(value) && value > 0 ? value : 1))
  }

  // On the detail page a typed quantity waits for Update, so one edit is one basket change, not one
  // per keystroke. A basket line sets the quantity outright as it is typed.
  const editing = withInput && amount > 0 && '' !== draft && draft !== String(amount)

  // A basket line commits the typed quantity once typing pauses, not on every keystroke.
  // The steps wait for that commit: a click inside the delay would be overwritten by it.
  const [pending, setPending] = useState(false)
  const [commitTyped, cancelCommit] = useDebouncedCallback((next: number) => {
    setPending(false)
    if (next !== amount) onAdd(next - amount)
  }, 500)

  const onDraftChange = (event: ChangeEvent<HTMLInputElement>) => {
    const raw = event.target.value.replace(/\D/g, '')
    // A number over the per-product limit is cut to it in the field too, not only in the basket.
    const typed = '' === raw ? raw : String(clamp(parseInt(raw, 10)))
    setDraft(typed)
    if (withInput || 0 === amount) return
    if ('' === typed) cancelCommit()
    else commitTyped(parseInt(typed, 10))
    setPending('' !== typed)
  }

  const onUpdate = () => {
    const next = parseInt(draft, 10)
    if (Number.isFinite(next) && next !== amount) onAdd(next - amount)
  }

  const input = (withInput || cartLine) && (
    <input
      type="text"
      inputMode="numeric"
      className={style.input}
      value={draft}
      disabled={disabled}
      onChange={onDraftChange}
      onBlur={() => setDraft(amount > 0 ? String(amount) : draft || '1')}
      onKeyDown={event => {
        if ('Enter' === event.key && editing) onUpdate()
      }}
      onClick={event => event.stopPropagation()}
      aria-label={t('questions', 'ec', 'tesco', 'quantity')}
      data-testid="SW-EC-QuantityInput"
    />
  )

  const minus = (
    <button
      type="button"
      className={style.step}
      disabled={disabled || pending}
      onClick={change(-1)}
      aria-label={t('questions', 'ec', 'tesco', 'decreaseQuantity')}
    >
      <i className="pi pi-minus" />
    </button>
  )

  const plus = (
    <button
      type="button"
      className={style.step}
      disabled={disabled || pending || atCap}
      onClick={change(1)}
      aria-label={t('questions', 'ec', 'tesco', 'increaseQuantity')}
    >
      <i className="pi pi-plus" />
    </button>
  )

  // A basket line always holds at least one, so it never falls back to Add.
  if (cartLine) {
    return (
      // The app's line shows the quantity as plain text between the steps; there is no field to type in.
      <div className={`${style.spinner} ${style.buyBox} ${style.cartLine} ${viewport.mobile ? style.mobile : ''}`}>
        {minus}
        {viewport.mobile ? <span className={style.qty}>{amount}</span> : input}
        {plus}
      </div>
    )
  }

  const steps = (
    <>
      {minus}
      {!withInput && <span className={style.qty}>{amount}</span>}
      {plus}
    </>
  )

  if (0 === amount) {
    const add = (
      <button
        type="button"
        className={`${style.add} ${viewport.mobile ? style.mobile : ''}`}
        disabled={disabled}
        onClick={withInput ? onAddDraft : change(1)}
        data-testid="SW-EC-AddToCart"
      >
        {t('questions', 'ec', 'tesco', 'add')}
      </button>
    )
    return withInput ? (
      <div className={style.buyBox}>
        {input}
        {add}
      </div>
    ) : (
      add
    )
  }

  const spinner = (
    <div
      className={`${style.spinner} ${'small' === appearance ? style.small : style.big} ${viewport.mobile ? style.mobile : ''} ${
        withInput ? style.buyBox : ''
      }`}
    >
      {input}
      {editing ? (
        <button
          type="button"
          className={style.add}
          // Keeps the focus in the field: its blur would reset the draft before the click lands.
          onMouseDown={event => event.preventDefault()}
          onClick={event => {
            event.stopPropagation()
            onUpdate()
          }}
          data-testid="SW-EC-UpdateQuantity"
        >
          {t('questions', 'ec', 'tesco', 'update')}
        </button>
      ) : (
        steps
      )}
    </div>
  )

  if (!withInput) return spinner

  return (
    <div className={style.inBasketBox}>
      {spinner}
      <div className={style.inBasket}>
        <span className={style.inBasketIcon}>
          <i className="pi pi-check" />
        </span>
        {t<(count: number) => string>('questions', 'ec', 'tesco', 'inBasket')(amount)}
      </div>
    </div>
  )
}

export default AddToCartButton
